// This React component renders the UPS banned drivers table, honoring global agency/search filters,
// relying on bansSlice as the read model and centralized socket-driven invalidation,
// and letting admins unban drivers while routing details through DriverDetailsModal.

import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { UserCheck, Eye } from 'lucide-react';

import { selectIsUpsAdmin } from '../auth/authSlice';
import { selectAllAgencies } from '../agencies/agenciesSlice';
import { selectFilters, bumpStatsRefreshKey } from '../ui/filtersSlice';
import { upsertDriver } from '../drivers/driverSlice';

import { unbanDriver } from '../../services/upsDriverService';
import { formatOwnerLabel } from './utils/formatOwnerLabel';
import DriverDetailsModal from './modals/DriverDetailsModal';
import DriverMobileCard from './components/DriverMobileCard';
import { COMPANY_ACTION_CLASSES } from './ui';

import {
  loadBannedDrivers,
  selectBannedRows,
  selectBansLoading,
  selectBansError,
} from '../bans/bansSlice';

export default function BannedDriversList() {
  const dispatch = useDispatch();
  const isAdmin = useSelector(selectIsUpsAdmin);

  const { agencyId, q, source } = useSelector(selectFilters);
  const agencies = useSelector(selectAllAgencies);

  const bannedRows = useSelector(selectBannedRows);
  const loading = useSelector(selectBansLoading);
  const error = useSelector(selectBansError);

  // Details modal state
  const [showDetails, setShowDetails] = useState(false);
  const [selectedDetailsRow, setSelectedDetailsRow] = useState(null);

  useEffect(() => {
    dispatch(loadBannedDrivers());
  }, [dispatch, agencyId, q, source]);

  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const focus = sp.get('focus');
    if (!focus) return;
    const el = document.querySelector(`[data-row-id="${focus}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('ring-2', 'ring-app-primary/40', 'animate-pulse');
    const t = setTimeout(() => {
      el.classList.remove('ring-2', 'ring-app-primary/40', 'animate-pulse');
      const next = new URLSearchParams(sp);
      next.delete('focus');
      setSp(next, { replace: true });
    }, 1600);
    return () => clearTimeout(t);
  }, [sp, setSp]);

  const handleUnban = async (row) => {
    try {
      const idForApi = row.ban_id ?? row.id;
      await unbanDriver(idForApi);
      dispatch(loadBannedDrivers());
      dispatch(bumpStatsRefreshKey());
    } catch (err) {
      console.error('Unban failed:', err);
    }
  };

  const openDetails = (row) => {
    dispatch(upsertDriver(row));
    setSelectedDetailsRow(row);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setSelectedDetailsRow(null);
  };

  const scopeLabel = (() => {
    if (!agencyId) return 'All Agencies';
    const a = (agencies || []).find(
      (x) => String(x.id ?? x.agency_id ?? x.agencyId) === String(agencyId)
    );
    return a?.name ?? a?.agency_name ?? `Agency #${agencyId}`;
  })();

  const hasSearch = (q || '').trim().length > 0;

  return (
    <>
      <section className="my-6 overflow-hidden rounded-2xl border border-app-border bg-app-card shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-app-border px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-app-text">Banned Drivers</h3>
            <p className="mt-1 text-sm text-app-muted">
              Review restricted drivers and restore access when appropriate.
            </p>
          </div>

          <div className="rounded-full bg-app-surface px-4 py-2 text-sm font-semibold text-app-muted">
            {(bannedRows || []).length} banned
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700">
            {error}
          </div>
        )}

        <div className="hidden overflow-x-auto xl:block">
          <table className="min-w-full bg-app-card">
            <thead className="border-b border-app-border bg-app-surface">
              <tr>
                {['Driver', 'Vehicle', 'Phone', 'Email', 'Owner', 'Banned At', 'Actions'].map((col) => (
                  <th
                    key={col}
                    className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.18em] text-app-muted"
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-app-border">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-app-muted">
                    Loading banned drivers…
                  </td>
                </tr>
              ) : (bannedRows || []).length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-sm text-app-muted">
                    No banned drivers{agencyId ? ` for ${scopeLabel}` : ''}
                    {hasSearch ? ` matching “${q}”` : ''}.
                  </td>
                </tr>
              ) : (
                (bannedRows || []).map((row) => (
                  <tr
                    key={row.ban_id ?? row.id}
                    data-row-id={row.id}
                    className="transition hover:bg-app-surface"
                  >
                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-red-200 bg-red-50 text-xs font-bold uppercase text-red-700">
                          {(row.name || '—')
                            .split(' ')
                            .map((part) => part[0])
                            .join('')
                            .slice(0, 2)}
                        </div>

                        <div>
                          <div className="font-semibold text-app-text">{row.name || '—'}</div>
                          <div className="text-xs text-app-muted">
                            Ban ID · {row.ban_id ? `BAN-${row.ban_id}` : '—'}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {row.vehicle_registration_number || '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {row.phone || '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-muted">
                      {row.email || '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {formatOwnerLabel(row)}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap text-sm text-app-text">
                      {row.banned_at ? new Date(row.banned_at).toLocaleString() : '—'}
                    </td>

                    <td className="px-6 py-5 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openDetails(row)}
                          className={COMPANY_ACTION_CLASSES.compactSecondary}
                          title="View details"
                        >
                          <Eye size={16} />
                        </button>

                        <button
                          onClick={() => handleUnban(row)}
                          disabled={!isAdmin}
                          title={isAdmin ? 'Unban driver' : 'Admins only'}
                          className={
                            isAdmin
                              ? COMPANY_ACTION_CLASSES.compactSuccess
                              : 'rounded-xl border px-3 py-2 text-sm font-semibold transition cursor-not-allowed border-app-border bg-app-surface text-app-muted/50'
                          }
                        >
                          <UserCheck size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="space-y-3 p-4 xl:hidden">
          {loading ? (
            <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
              Loading banned drivers...
            </div>
          ) : (bannedRows || []).length === 0 ? (
            <div className="rounded-2xl border border-app-border bg-app-surface px-4 py-10 text-center text-sm text-app-muted">
              No banned drivers{agencyId ? ` for ${scopeLabel}` : ''}
              {hasSearch ? ` matching "${q}"` : ''}.
            </div>
          ) : (
            (bannedRows || []).map((row) => {
              const avatarText = (row.name || '')
                .split(' ')
                .map((part) => part[0])
                .join('')
                .slice(0, 2);

              return (
                <div key={row.ban_id ?? row.id} data-row-id={row.id}>
                  <DriverMobileCard
                    tone="banned"
                    title={row.name || '—'}
                    subtitle={`Ban ID · ${row.ban_id ? `BAN-${row.ban_id}` : '—'}`}
                    avatarText={avatarText}
                    fields={[
                      ['Vehicle', row.vehicle_registration_number],
                      ['Owner', formatOwnerLabel(row)],
                      ['Phone', row.phone],
                      ['Email', row.email],
                      ['Ban Reason', row.ban_reason || row.reason],
                      ['Ban Scope', row.ban_scope],
                      ['Banned At', row.banned_at ? new Date(row.banned_at).toLocaleString() : null],
                    ]}
                    actions={
                      <>
                        <button
                          onClick={() => openDetails(row)}
                          className={COMPANY_ACTION_CLASSES.compactSecondary}
                          title="View details"
                        >
                          <Eye size={16} />
                        </button>

                        <button
                          onClick={() => handleUnban(row)}
                          disabled={!isAdmin}
                          title={isAdmin ? 'Unban driver' : 'Admins only'}
                          className={
                            isAdmin
                              ? COMPANY_ACTION_CLASSES.compactSuccess
                              : 'rounded-xl border px-3 py-2 text-sm font-semibold transition cursor-not-allowed border-app-border bg-app-surface text-app-muted/50'
                          }
                        >
                          <UserCheck size={16} />
                        </button>
                      </>
                    }
                  />
                </div>
              );
            })
          )}
        </div>
      </section>

      {showDetails && selectedDetailsRow && (
        <DriverDetailsModal driver={selectedDetailsRow} onClose={closeDetails} />
      )}
    </>
  );
}
